import { Box, Flex, Text } from '@minetrace/ui'
import { LuTriangleAlert as AlertTriangle, LuExternalLink as ExternalLink, LuShield as Shield } from 'react-icons/lu'
import type { RestrictionCheckSummary } from '../api/models/response/restrictionCheck'
import { restrictionClassColor, restrictionClassLabel, sourceLabel } from '../api/models/response/restrictionCheck'

interface RestrictionWarningCardProps {
  check: RestrictionCheckSummary | null
  // When false, hide the "no intersections" success state and just render nothing.
  // Useful in the evaluator panel where empty checks shouldn't take space.
  showEmptyState?: boolean
  // Cuando se provee, la tarjeta ofrece re-ejecutar el screening. Se omite en las vistas
  // de solo lectura, donde no hay nada que recalcular.
  onRecheck?: () => void | Promise<void>
  rechecking?: boolean
}

// Card no bloqueante que muestra el resultado del check espacial contra
// el catálogo de zonas restringidas. Renderiza:
//  - Si hay intersecciones: un panel rojo/naranja/amarillo con cada zona,
//    su fuente, norma legal y % de solape.
//  - Si no hay intersecciones: un mensaje verde "screening limpio".
//  - Si todavía no hay check (null): nada.
// Política: aviso informativo, no bloquea el wizard ni la aprobación.
const RestrictionWarningCard = ({ check, showEmptyState = true, onRecheck, rechecking = false }: RestrictionWarningCardProps) => {
  if (!check) return null

  const botonRechequear = onRecheck ? (
    <Box
      as="button"
      type="button"
      mt={2}
      px={3}
      py={1}
      borderRadius="md"
      border="1px solid #cbd5e0"
      bg="white"
      color="#2d3748"
      fontSize="11px"
      fontWeight="700"
      cursor={rechecking ? 'default' : 'pointer'}
      opacity={rechecking ? 0.6 : 1}
      _hover={rechecking ? {} : { bg: '#edf2f7' }}
      onClick={() => { if (!rechecking) void onRecheck() }}
      disabled={rechecking}
    >
      {rechecking ? 'Chequeando...' : 'Volver a chequear'}
    </Box>
  ) : null

  // Un chequeo obsoleto gana sobre las otras dos ramas: tanto el panel de intersecciones
  // como el "screening limpio" afirmarían algo sobre el polígono actual que este análisis
  // no puede sostener, porque se corrió sobre otra geometría.
  if (check.isStale) {
    return (
      <Box mt={4} p={4} borderRadius="xl" border="1px solid #cbd5e0" bg="#f7fafc">
        <Flex gap={3} alignItems="flex-start">
          <AlertTriangle size={20} color="#4a5568" style={{ flexShrink: 0, marginTop: 2 }} />
          <Box>
            <Text fontSize="13px" fontWeight="700" color="#2d3748">
              Screening de zonas restringidas — desactualizado
            </Text>
            <Text fontSize="12px" color="#4a5568" mt={0.5}>
              El último análisis ({new Date(check.checkedAt).toLocaleString('es-PE')}) se corrió
              sobre un polígono distinto al actual, así que no describe la georreferenciación
              vigente. Vuelve a chequear antes de aprobar.
            </Text>
            {botonRechequear}
          </Box>
        </Flex>
      </Box>
    )
  }

  if (check.totalIntersectingZones === 0) {
    if (!showEmptyState) return null
    return (
      <Box mt={4} p={4} borderRadius="xl" border="1px solid #c6f6d5" bg="#f0fff4">
        <Flex gap={3} alignItems="center">
          <Shield size={20} color="#22543d" />
          <Box>
            <Text fontSize="13px" fontWeight="700" color="#22543d">
              Screening de zonas restringidas — sin intersecciones
            </Text>
            <Text fontSize="12px" color="#22543d" mt={0.5}>
              El polígono no superpone ninguna zona restringida conocida ({new Date(check.checkedAt).toLocaleString('es-PE')}).
            </Text>
            {botonRechequear}
          </Box>
        </Flex>
      </Box>
    )
  }

  // Color del banner según la clase más alta detectada.
  const palette = check.highestClass ? restrictionClassColor(check.highestClass) : 'gray'
  const bannerBg =
    palette === 'red' ? '#fff5f5' :
    palette === 'orange' ? '#fffaf0' :
    palette === 'yellow' ? '#fcf6e8' :
    palette === 'amber' ? '#fffaf0' :
    '#f7fafc'
  const bannerBorder =
    palette === 'red' ? '#feb2b2' :
    palette === 'orange' ? '#fbd38d' :
    palette === 'yellow' ? '#fde68a' :
    palette === 'amber' ? '#fbd38d' :
    '#cbd5e0'
  const bannerText =
    palette === 'red' ? '#9b2c2c' :
    palette === 'orange' ? '#9c4221' :
    palette === 'yellow' ? '#744210' :
    palette === 'amber' ? '#9c4221' :
    '#2d3748'

  return (
    <Box mt={4} p={4} borderRadius="xl" border="1px solid" borderColor={bannerBorder} bg={bannerBg}>
      <Flex gap={3} alignItems="flex-start" mb={3}>
        <AlertTriangle size={22} color={bannerText} style={{ flexShrink: 0, marginTop: 2 }} />
        <Box flex={1}>
          <Text fontSize="13px" fontWeight="700" color={bannerText}>
            Atención: el polígono interseca {check.totalIntersectingZones} zona{check.totalIntersectingZones === 1 ? '' : 's'} restringida{check.totalIntersectingZones === 1 ? '' : 's'}
          </Text>
          <Text fontSize="11px" color={bannerText} opacity={0.85} mt={0.5}>
            Información referencial — no bloquea el envío, pero debe revisarse antes de aprobar. Check realizado el {new Date(check.checkedAt).toLocaleString('es-PE')}.
          </Text>
          {botonRechequear}
        </Box>
      </Flex>

      <Box bg="white" borderRadius="lg" overflow="hidden">
        {check.intersections.map((i, idx) => {
          const cls = i.restrictionClass
          const clsColor = restrictionClassColor(cls)
          const pillBg =
            clsColor === 'red' ? '#fed7d7' :
            clsColor === 'orange' ? '#feebc8' :
            clsColor === 'yellow' ? '#f9eccf' :
            clsColor === 'amber' ? '#feebc8' :
            '#e8dfd1'
          const pillText =
            clsColor === 'red' ? '#742a2a' :
            clsColor === 'orange' ? '#7b341e' :
            clsColor === 'yellow' ? '#744210' :
            clsColor === 'amber' ? '#7b341e' :
            '#2d3748'

          return (
            <Box
              key={i.intersectionId}
              p={3}
              borderTop={idx === 0 ? 'none' : '1px solid #edf2f7'}
            >
              <Flex justifyContent="space-between" alignItems="flex-start" gap={3}>
                <Box flex={1} minW={0}>
                  <Flex gap={2} alignItems="center" mb={1} flexWrap="wrap">
                    <Text fontSize="13px" fontWeight="700" color="#2a2622">
                      {i.zoneName}
                    </Text>
                    <Box px="8px" py="1px" borderRadius="full" fontSize="10px" fontWeight="700" bg={pillBg} color={pillText} textTransform="uppercase" letterSpacing="0.04em">
                      {restrictionClassLabel(cls)}
                    </Box>
                    {i.isReferential && (
                      <Box px="8px" py="1px" borderRadius="full" fontSize="10px" fontWeight="600" bg="#edf2f7" color="#4a5568" textTransform="uppercase" letterSpacing="0.04em">
                        Referencial
                      </Box>
                    )}
                  </Flex>
                  <Text fontSize="11px" color="#857b71" mb={1}>
                    {sourceLabel(i.source)}{i.category ? ` · ${i.category}` : ''}{i.entidad ? ` · ${i.entidad}` : ''}
                  </Text>
                  {i.norma && (
                    <Text fontSize="11px" color="#4a5568" fontFamily="monospace">
                      {i.norma}
                    </Text>
                  )}
                </Box>
                <Box textAlign="right" flexShrink={0}>
                  <Text fontSize="14px" fontWeight="800" color="#2a2622" lineHeight="1">
                    {i.overlapPercentage.toFixed(1)}%
                  </Text>
                  <Text fontSize="10px" color="#a0aec0" mt={0.5}>
                    {Math.round(i.intersectionAreaM2 / 10000).toLocaleString('es-PE')} ha
                  </Text>
                  {i.sourceUrl && (
                    <a href={i.sourceUrl} target="_blank" rel="noreferrer" style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10, color: '#3182ce' }}>
                      Fuente <ExternalLink size={10} />
                    </a>
                  )}
                </Box>
              </Flex>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default RestrictionWarningCard
